import * as React from 'react'
import classnames from 'classnames'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'

import Moment from 'react-moment'
import { Input, Table, Button, FormGroup } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import withConfirm, { WithConfirmProps } from 'src/lib/helpers/withConfirm'

import { RootState } from '../../store/rootReducer'
import { File } from '../../lib/interfaces/File.interface'
import { JobApplicant } from '../../lib/interfaces/JobApplicant.interface'
import { User, hasAnyPermission, hasPermission } from '../../lib/interfaces/User.interface'
import { JobApplicantComment } from '../../lib/interfaces/JobApplicantComment.interface'
import {
  ApplicationStatus,
  applicationStatusToString
} from '../../lib/interfaces/ApplicationStatus.interface'

import ApplicationEditionModal from './components/ApplicationEdition.modal'
import ApplicationProgressBar from './components/ApplicationProgressBar.component'
import ConvertApplicantToEmployeeModal from './components/ConvertApplicantToEmployee.modal'

import Breadcrumbs from '../Breadcrumbs.component'
import FileList from 'src/common/FileList.component'
import FileDropzone from 'src/common/FileDropzone.component'
import LocationImage from '../../common/LocationImage.component'
import EntityCommentList from 'src/common/EntityCommentList.component'
import CopyToClipboardButton from 'src/common/CopyToClipboardButton.component'

import * as applicationsActions from '../../store/applications/actions'

import { formatName, formatAddress } from 'src/lib/helpers/format'

const get = require('lodash/get')
const map = require('lodash/map')
const sortBy = require('lodash/sortBy')
const isEmpty = require('lodash/isEmpty')
const includes = require('lodash/includes')

interface RouteInfo {
  applicationId: string
}

export interface ApplicationDetailsProps extends WithConfirmProps, RouteComponentProps<RouteInfo> {
  user: User
  isFetchingFiles: boolean
  isUploadingFile: boolean
  isPostingComment: boolean
  application?: JobApplicant
  fetchApplicationFiles: (applicationId: string, next?: (err?: any, files?: File[]) => void) => void
  fetchApplication: (applicationId: string, next?: (err?: any, application?: JobApplicant) => void) => void
  uploadApplicationFile: (applicationId: string, file: any, next?: (err?: any, file?: File) => void) => void
  approveApplication: (applicationId: string, next?: (err?: any, application?: JobApplicant) => void) => void
  postApplicationComment: (applicationId: string, comment: string, next?: (err?: any, comment?: JobApplicantComment) => void) => void
  updateApplicationStatus: (applicationId: string, status: ApplicationStatus, next?: (err?: any, application?: JobApplicant) => void) => void
}

export interface ApplicationDetailsState {
  comment: string
  applicantToConvert?: JobApplicant
  applicationEditionModalOpen: boolean
}

class ApplicationDetails extends React.PureComponent<ApplicationDetailsProps, ApplicationDetailsState> {
  constructor(props: ApplicationDetailsProps) {
    super(props)

    this.state = {
      comment: '',
      applicantToConvert: undefined,
      applicationEditionModalOpen: false
    }

    this.handleDeny = this.handleDeny.bind(this)
    this.handleNextStep = this.handleNextStep.bind(this)
    this.handleConvertToEmployee = this.handleConvertToEmployee.bind(this)
  }

  get applicationId() {
    return this.props.match.params.applicationId
  }

  get applicantName() {
    const { application } = this.props
    return formatName(application)
  }

  get applicationLocation() {
    const { application } = this.props
    if (!application) {
      return null
    }

    return application.jobPosition && application.jobPosition.location;
  }

  get applicationFormattedAddress() {
    const { application } = this.props
    return formatAddress(application!.state, application!.city, application!.street)
  }

  componentDidMount() {
    this.fetchApplication()
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.applicationId !== prevProps.match.params.applicationId) {
      this.fetchApplication()
    }
  }

  fetchApplication() {
    this.props.fetchApplication(this.applicationId, (error?: any, application?: JobApplicant) => {
      if (!error) {
        this.fetchFiles()
      }
    })
  }

  fetchFiles = () => {
    this.props.fetchApplicationFiles(this.applicationId)
  }

  handleFileUpload = file => {
    const { uploadApplicationFile } = this.props
    uploadApplicationFile(this.applicationId, file, (err?: any, uploadedFile?: File) => {
      if (!err) { this.fetchFiles() }
    })
  }

  handleCommentSubmit = e => {
    e.preventDefault()

    const { comment } = this.state
    const { postApplicationComment } = this.props

    this.setState(
      { comment: '' },
      () => postApplicationComment(this.applicationId, comment)
    )
  }

  handleDeny(application: JobApplicant) {
    this.props.confirm(
      () => this.props.updateApplicationStatus(this.applicationId, ApplicationStatus.DENIED),
      {
        description:
          <span>
            Are you sure you want to <b>deny</b> this application?
          </span>
      })
  }

  handleNextStep(application: JobApplicant, nextStatus: ApplicationStatus) {
    if (nextStatus === ApplicationStatus.APPROVED) {
      this.handleApproveApplication(application)
      return
    }

    const nextStatusString = applicationStatusToString(nextStatus)

    this.props.confirm(
      () => this.props.updateApplicationStatus(this.applicationId, nextStatus),
      {
        description:
          <span>
            Are you sure you want to update the status to "<b>{nextStatusString}</b>"?
          </span>
      }
    )
  }

  handleApproveApplication(application: JobApplicant) {
    this.props.confirm(
      () => this.props.approveApplication(this.applicationId),
      {
        description:
          <span>
            Are you sure you want to <b>approve</b> this application?
          </span>
      }
    )
  }

  handleConvertToEmployee(application: JobApplicant) {
    this.setState({ applicantToConvert: application })
  }

  render() {
    const {
      applicantToConvert,
      applicationEditionModalOpen
    } = this.state
    const {
      user,
      application,
      isFetchingFiles,
      isUploadingFile,
      isPostingComment
    } = this.props

  
    const applicationLocation = this.applicationLocation

    return (
      <div>
        <Breadcrumbs breadcrumbs={[
          { name: 'Dashboard', link: '/dashboard' },
          { name: 'Applications', link: '/applications' },
          { name: this.applicantName || this.applicationId, active: true }
        ]} />

        <ConvertApplicantToEmployeeModal
          open={!!applicantToConvert}
          application={applicantToConvert}
          onClose={() => this.setState({ applicantToConvert: undefined })}
        />

        {application && (
          <div className="application-page">
            <ApplicationEditionModal
              application={application}
              open={applicationEditionModalOpen}
              onClose={() => this.setState({ applicationEditionModalOpen: false })}
            />

            <section className="container-fluid pl-0 pr-0 ml-0">
              <div className="d-flex align-items-center mb-2">
                <h5 className="title mb-0">{this.applicantName}</h5>

                {hasPermission(user, 'ROLE_APPLICATION_ADMIN') && (
                  <Button
                    size="sm"
                    color="light"
                    className="ggbutton ml-3"
                    onClick={() => this.setState({ applicationEditionModalOpen: true })}
                  >
                    Edit
                  </Button>
                )}
              </div>

              <div className="row no-gutters mt-3">
                <div className="col-12 col-md-5 col-lg-3 pr-md-3">
                  <div className="ggcard p-3">
                    <table>
                      <tbody>
                        <tr>
                          <td>Status:</td>
                          <td>
                            <div className={classnames('badge badge-primary',
                              { 'badge-danger': includes([ApplicationStatus.STARTED, ApplicationStatus.DENIED, ApplicationStatus.CLOSED], application.status) }
                            )}>
                              {applicationStatusToString(application.status)}
                            </div>

                            {/* <ChangeableBadge
                              enableChange={true}
                              containerMinWidth="90px"
                              value={application.status}
                              options={orderedApplicationStatus}
                              changePermissions={['ROLE_ADMIN']}
                              getOptionValue={status => status}
                              getOptionLabel={applicationStatusToString}
                              badgeText={applicationStatusToString(application.status)}
                              onChange={newStatus => this.handleNextStep(application, newStatus)}
                              className={classnames(
                                'badge-primary',
                                {
                                  'badge-danger': includes([
                                    ApplicationStatus.STARTED,
                                    ApplicationStatus.DENIED,
                                    ApplicationStatus.CLOSED
                                  ], application.status)
                                }
                              )}
                            /> */}
                          </td>
                        </tr>
                        <tr>
                          <td>Date applied:</td>
                          <td><Moment date={application.dateCreated} format="MM.DD.YYYY" /></td>
                        </tr>
                        <tr>
                          <td>Previous employee ID:</td>
                          <td>{application.previousEmployeeId || '-'}</td>
                        </tr>
                        <tr>
                          <td>Date of birth:</td>
                          <td>
                            {application.dateOfBirth
                              ? <Moment date={application.dateOfBirth} format="MM.DD.YYYY" />
                              : '-'
                            }
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-12 col-md-7 col-lg-6 pr-md-3">
                  <div className="ggcard justify-content-center h-100 p-3">
                    <table>
                      <tbody>
                        <tr>
                          <td>Email address:</td>
                          <td>
                            <div className="d-flex align-items-center">
                              {!isEmpty(application.email) ? application.email : '-'}
                              {!isEmpty(application.email) && <CopyToClipboardButton className="ml-3" text={application.email} />}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td>Phone number:</td>
                          <td>
                            <div className="d-flex align-items-center">
                              {!isEmpty(application.phone) ? application.phone : ''}
                              {!isEmpty(application.phone) && <CopyToClipboardButton className="ml-3" text={application.phone} />}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td>Applied for:</td>
                          <td>
                            {application.jobPosition &&
                              `${application.jobPosition.jobPositionName} (${application.jobPosition.location && application.jobPosition.location.code})`
                            }
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {applicationLocation &&
                  <div className="col-12 col-md-4 col-lg-3 pr-lg-3 mt-3 mt-md-0">
                    <div className="ggcard h-100" style={{ minHeight: '125px' }}>
                      <LocationImage locationCode={applicationLocation.code} />
                    </div>
                  </div>
                }
              </div>
            </section>

            <section className="container-fluid pl-0 pr-0 ml-0">
              <div className="row no-gutters mt-3">
                <div className="col-12 col-md-5 col-lg-6 pr-md-3">
                  <div className="ggcard p-3">
                    <table>
                      <tbody>
                        <tr>
                          <td>Driver's license number:</td>
                          <td>
                            <div className="d-flex align-items-center">
                              {!isEmpty(application.driverLicenseNumber) ? application.driverLicenseNumber : '-'}
                              {!isEmpty(application.driverLicenseNumber) && <CopyToClipboardButton className="ml-3" text={application.driverLicenseNumber} />}
                            </div>
                          </td>
                        </tr>
                        <tr>
                        <td>Driver's license expiration:</td>
                          <td>
                            {application.driverLicenseExpirationDate
                              ? <Moment date={application.driverLicenseExpirationDate} format="MM.DD.YYYY" />
                              : '-'
                            }
                          </td>
                        </tr>
                        <tr>
                          <td>State:</td>
                          <td>
                            <div className="d-flex align-items-center">
                              {!isEmpty(application.state) ? application.state : '-'}
                              {!isEmpty(application.state) && <CopyToClipboardButton className="ml-3" text={application.state} />}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td>City:</td>
                          <td>
                            <div className="d-flex align-items-center">
                              {!isEmpty(application.city) ? application.city : '-'}
                              {!isEmpty(application.city) && <CopyToClipboardButton className="ml-3" text={application.city} />}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td>Street address:</td>
                          <td>
                            <div className="d-flex align-items-center">
                              {!isEmpty(application.street) ? application.street : '-'}
                              {!isEmpty(application.street) && <CopyToClipboardButton className="ml-3" text={application.street} />}
                            </div>
                          </td>
                        </tr>
                        <tr>
                          <td>Full address:</td>
                          <td>
                            <div className="d-flex align-items-center">
                              {!isEmpty(this.applicationFormattedAddress) ? this.applicationFormattedAddress : '-'}
                              {!isEmpty(this.applicationFormattedAddress) && <CopyToClipboardButton className="ml-3" text={this.applicationFormattedAddress} />}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </section>

            <section className="container-fluid pl-0 pr-0 mt-5 ml-0">
              <h5 className="title">Application Progress</h5>

              <div className="w-100 mt-3">
                <ApplicationProgressBar
                  onDeny={this.handleDeny}
                  application={application}
                  onNextStep={this.handleNextStep}
                  onConvertToEmployee={this.handleConvertToEmployee}
                />
              </div>
            </section>

            <section className="container-fluid row pl-0 pr-0 mt-5 ml-0">
              <div className="col-6 pl-0">
                <h6 className="title">Work Experience</h6>
                <Table className="ggtable">
                  <thead>
                    <tr>
                      <th>Position</th>
                      <th>Company</th>
                      <th>Start - End Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    {map(sortBy(application.workHistory, 'startDate'), workHistory => (
                      <tr>
                        <td>{workHistory.position}</td>
                        <td>{workHistory.company}</td>
                        <td>
                          <Moment date={workHistory.startDate} format="MM/YY" />
                          {' - '}
                          <Moment date={workHistory.endDate} format="MM/YY" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <div className="col-6 pr-0">
                <h6 className="title">Education History</h6>
                <Table className="ggtable">
                  <thead>
                    <tr>
                      <th>Degree</th>
                      <th>Institution</th>
                      <th>Start - End Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    {map(sortBy(application.educationHistory, 'startDate'), educationHistory => (
                      <tr>
                        <td>{educationHistory.degree}</td>
                        <td>{educationHistory.institution}</td>
                        <td>
                          <Moment date={educationHistory.startDate} format="MM/YY" />
                          {' - '}
                          <Moment date={educationHistory.endDate} format="MM/YY" />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </section>

            <section className="container-fluid pl-0 pr-0 mt-5 ml-0">
              <h5 className="title">Application History</h5>

              <div className="w-100 mt-3">
                <div className="d-flex flex-column">
                  <EntityCommentList comments={application.comments} />

                  <div className="ggcard p-3 mt-3">
                    <span className="text-uppercase"><b>Add an update:</b></span>
                    <FormGroup className="mt-3 mb-0">
                      <Input
                        type="textarea"
                        value={this.state.comment}
                        onChange={e => this.setState({ comment: e.target.value })}
                      />

                      <Button
                        type="submit"
                        color="primary"
                        className="btn w-100 mt-4"
                        onClick={this.handleCommentSubmit}
                        disabled={isPostingComment || isEmpty(this.state.comment)}
                      >
                        {isPostingComment &&
                          <FontAwesomeIcon
                            spin
                            className="mr-3"
                            icon={['fas', 'spinner']}
                          />
                        }
                        Submit
                      </Button>
                    </FormGroup>
                  </div>
                </div>
              </div>
            </section>

            <section className="pl-0 mt-5 mb-4 ml-0">
              <div className="d-flex align-items-center mb-2">
                <h5 className="title mb-0">Application Files</h5>
                {isFetchingFiles && 
                  <FontAwesomeIcon
                    spin
                    className="ml-2"
                    icon={['fas', 'spinner']}
                    style={{ fontSize: '20px' }}
                  />
                }
              </div>

              <div className="mt-4">
                <FileList
                  entity="application"
                  files={application.files}
                  entityId={application.id}
                  onDelete={this.fetchFiles}
                  className="row no-gutters"
                  fileWrapperClassName="col-4 pr-3 pb-3"
                  canDelete={hasAnyPermission(user, ['ROLE_JOB_APPLICATION', 'ROLE_APPLICATION_ADMIN'])}
                />
                
                <div className="w-100 mt-4">
                  <FileDropzone isLoading={isUploadingFile} onDrop={this.handleFileUpload} />
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    )
  }
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user,
  application: state.applications.applicationDetails,
  isFetchingFiles: state.applications.isFetchingFiles,
  isUploadingFile: state.applications.isUploadingFile,
  isPostingComment: state.applications.isPostingComment
})

const mapDispatchToProps = (dispatch) => ({
  fetchApplication: (applicationId: string, next?: (err?: any, application?: JobApplicant) => void) =>
    dispatch(applicationsActions.fetchApplication(applicationId, next)),
  fetchApplicationFiles: (applicationId: string, next?: (err?: any, files?: File[]) => void) =>
    dispatch(applicationsActions.fetchApplicationFiles(applicationId, next)),
  uploadApplicationFile: (applicationId: string, file: any, next?: (err?: any, file?: File) => void) =>
    dispatch(applicationsActions.uploadApplicationFile(applicationId, file, next)),
  approveApplication: (applicationId: string, next?: (err?: any, application?: JobApplicant) => void) =>
        dispatch(applicationsActions.approveApplication(applicationId, next)),
  postApplicationComment: (applicationId: string, comment: string, next?: (err?: any, comment?: JobApplicantComment) => void) =>
    dispatch(applicationsActions.postApplicationComment(applicationId, comment, next)),
  updateApplicationStatus: (applicationId: string, status: ApplicationStatus, next?: (err?: any, application?: JobApplicant) => void) =>
    dispatch(applicationsActions.updateApplicationStatus(applicationId, status, next))
})

export default withConfirm(withRouter(connect(mapStateToProps, mapDispatchToProps)(ApplicationDetails)))



// WEBPACK FOOTER //
// ./src/routes/applications/ApplicationDetails.page.tsx